import type { SSOProvider, SSOProviderRequest } from '@central/api';
import { Card, StatusChip } from '@central/ui';
import { Button, Table, VisuallyHidden } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { keys, useApi } from '../api';
import { DataTable, Empty, QueryState } from '../components/parts';
import { SsoProviderEditor, type EditableProvider } from '../components/SsoProviderEditor';
import { mono } from '../lib/labels';
import { ssoKindLabel } from '../lib/sso';
import { useCanWrite } from '../lib/me';

/** Single sign-on card on customer detail: the customer's OIDC and SAML identity providers. */
export function CustomerSso({ customerId }: { customerId: string }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const providers = useQuery({
    queryKey: keys.customerSso(customerId),
    queryFn: () => api.customers.sso.list(customerId),
  });
  // undefined: closed; null: a new provider; a provider: that one.
  const [editing, setEditing] = useState<EditableProvider | null>();
  const items = providers.data?.items ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.customerSso(customerId) });

  return (
    <Card
      title="Single sign-on"
      padded={false}
      action={
        canWrite && (
          <Button size="xs" color="dark" onClick={() => setEditing(null)}>
            Add provider
          </Button>
        )
      }
    >
      <QueryState isPending={providers.isPending} error={providers.error}>
        {() =>
          items.length === 0 ? (
            <Empty>
              No identity providers. Portal users sign in with a password and second factor.
            </Empty>
          ) : (
            <DataTable
              label="Identity providers"
              head={['Name', 'Type', 'Email domains', 'Status', '']}
            >
              {items.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td style={{ fontWeight: 600 }}>{p.name}</Table.Td>
                  <Table.Td>{ssoKindLabel[p.kind]}</Table.Td>
                  <Table.Td style={mono}>{p.domains.join(', ')}</Table.Td>
                  <Table.Td>
                    <StatusChip tone={p.enabled ? 'ok' : 'unk'}>
                      {p.enabled ? 'Enabled' : 'Disabled'}
                    </StatusChip>
                  </Table.Td>
                  <Table.Td>
                    <Button variant="subtle" size="xs" onClick={() => setEditing(p)}>
                      {canWrite ? 'Edit' : 'View'} <VisuallyHidden>{p.name}</VisuallyHidden>
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </DataTable>
          )
        }
      </QueryState>
      {editing !== undefined && (
        <SsoProviderEditor
          provider={editing}
          readOnly={!canWrite}
          idpOwner="the customer’s identity provider"
          save={async (body, id) => {
            const b = body as SSOProviderRequest;
            const p: SSOProvider = id
              ? await api.customers.sso.update(customerId, id, b)
              : await api.customers.sso.create(customerId, b);
            await refresh();
            return p;
          }}
          remove={async (id) => {
            await api.customers.sso.delete(customerId, id);
            await refresh();
          }}
          onSaved={setEditing}
          onClose={() => setEditing(undefined)}
        />
      )}
    </Card>
  );
}
